import type { DialogProps } from "@mui/material/Dialog";
import { RavenDialog } from "@ravenappdev/raven-ui";
import type { ReactNode } from "react";

export interface ModalProps extends Omit<DialogProps, "title"> {
  title?: string;
  actions?: ReactNode;
  onClose?: () => void;
}

// The package builds the title / content / actions structure but styles it
// from `raven-ui.css`, which ESS does not load (CLAUDE.md §6). These three
// rules are what that stylesheet would have supplied and what ESS's own
// composition used to set inline: the close button pushed to the right edge
// of the title row, its dense padding, and the `dividers` hairlines around
// the scrollable content. The `MuiDialogTitle-root +` prefix is needed
// because MUI zeroes an undividered content's top padding at that
// specificity.
const DIALOG_STRUCTURE_SX = {
  "& .raven-dialog__title": {
    display: "flex",
    alignItems: "center",
    pr: 1,
    pb: 1,
  },
  "& .raven-dialog__close": { ml: "auto", p: "5px" },
  "& .MuiDialogTitle-root + .raven-dialog__content": {
    px: 3,
    py: 2,
    borderTop: "1px solid",
    borderBottom: "1px solid",
    borderColor: "divider",
  },
} as const;

export default function Modal({ sx, ...rest }: ModalProps) {
  return (
    <RavenDialog
      fullWidth
      maxWidth="sm"
      sx={[DIALOG_STRUCTURE_SX, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    />
  );
}
